import { Ionicons } from '@expo/vector-icons';
import { format } from 'date-fns';
import { router } from 'expo-router';
import React, { useState } from 'react';
import { Modal, Platform, Pressable, TextInput, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { CollapseSection } from '@/components/enrollments/collapse-section';
import { ThemedText } from '@/components/themed-text';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/ui/error-state';
import { FadeInView } from '@/components/ui/fade-in-view';
import { PageLayout } from '@/components/ui/page-layout';
import { SkeletonForm } from '@/components/ui/skeleton';
import {
  ABOUT_ACTIVATE_BUTTON,
  ABOUT_COMING_SOON_NOTE,
  ABOUT_COMPARISON_ROWS,
  ABOUT_CONTINUE_ACTIVATION_BUTTON,
  ABOUT_INTRO,
  ABOUT_LIMITS_NOTE,
  ABOUT_SETTLEMENT_NOTE,
  ABOUT_SIMULATOR,
  ABOUT_WHAT_WE_ASK,
  AboutDetailRow,
  getAboutFeeNote,
  getAboutFeeRows,
  getAboutLimitRows,
  getAboutSettlementRows,
  getSimulatorRejectReason,
  PAYMENT_ABOUT_FAQ,
  SETTINGS_METHODS,
} from '@/content/payments';
import { usePaymentAccount } from '@/hooks/use-payment-account';
import { usePaymentPricing } from '@/hooks/use-payment-pricing';
import { PAYMENT_ACCOUNT_STATUS, PaymentPricing } from '@/types/payment-account';
import { PAYMENT_METHOD } from '@/types/payment-method';
import { getApiErrorMessage } from '@/utils/api-error';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format-currency';
import { parseCurrency } from '@/utils/parse-currency';
import { formatFeePercent, simulatePayment } from '@/utils/payment-simulator';
import { useThemedHex } from '@/hooks/use-theme-colors';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { KEYBOARD_BOTTOM_OFFSET } from '@/components/dashboard-scroll-view';

/** Formas simuladas, na ordem do comparativo (seção 6.2, item 2). */
const SIMULATOR_METHODS = [
  { method: PAYMENT_METHOD.PIX, label: SETTINGS_METHODS.pixLabel },
  { method: PAYMENT_METHOD.CREDIT_CARD, label: SETTINGS_METHODS.cardLabel },
  { method: PAYMENT_METHOD.BANK_SLIP, label: SETTINGS_METHODS.boletoLabel },
] as const;

type DetailKey = 'diff' | 'methods' | 'fee' | 'settlement' | 'simulate' | 'ask' | 'faq';

/** "1234,56" — o formulário nunca guarda o "R$", só os dígitos formatados. */
function maskCurrencyDigits(text: string): string {
  const digits = text.replace(/\D/g, '');
  if (!digits) return '';
  const value = parseInt(digits, 10) / 100;
  return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function goBack() {
  if (router.canGoBack()) {
    router.back();
    return;
  }
  router.replace('/(tabs)/(provider)/profile' as never);
}

/**
 * "Como funciona" (plano, seção 6.2, T4.2) no formato de lista de ajustes:
 * cada linha responde uma pergunta em poucas palavras e abre o detalhe num
 * modal. Serve também a quem já ativou (sem o botão). Nenhum número é escrito
 * aqui — tudo vem de `usePaymentPricing()` via `app/content/payments/about.ts`.
 */
export default function PaymentsAboutScreen() {
  const { data: pricing, isLoading, error, refetch } = usePaymentPricing();
  const { data: accountStatus, isLoading: isAccountLoading } = usePaymentAccount();
  const insets = useSafeAreaInsets();

  const status = accountStatus?.account?.status ?? null;
  const canShowActivate = !!pricing?.canActivate && !isAccountLoading;
  const ctaTitle = !canShowActivate
    ? null
    : status === null
      ? ABOUT_ACTIVATE_BUTTON
      : status === PAYMENT_ACCOUNT_STATUS.DRAFT
        ? ABOUT_CONTINUE_ACTIVATION_BUTTON
        : null;

  return (
    <PageLayout
      title="Como funciona"
      onBack={goBack}
      footer={
        ctaTitle ? (
          <View
            className="border-t border-neutral-200 bg-background px-4 pt-3"
            style={{ paddingBottom: Math.max(insets.bottom, 16) }}
          >
            <Button
              title={ctaTitle}
              size="lg"
              className="w-full self-center"
              style={{ maxWidth: 560 }}
              onPress={() => router.push('/(tabs)/(provider)/payments/activate' as never)}
              testID={
                status === null
                  ? 'payments-about-activate-button'
                  : 'payments-about-continue-activation-button'
              }
            />
          </View>
        ) : undefined
      }
    >
      <FadeInView>
        <ThemedText className="text-sm leading-5 text-neutral-500">{ABOUT_INTRO}</ThemedText>
      </FadeInView>

      {isLoading ? (
        <SkeletonForm fields={4} showSubmit={false} />
      ) : error || !pricing ? (
        <ErrorState
          variant="inline"
          title="Não foi possível carregar"
          message={getApiErrorMessage(error, 'Tente de novo em instantes.')}
          onRetry={refetch}
        />
      ) : (
        <AboutList pricing={pricing} />
      )}
    </PageLayout>
  );
}

function AboutList({ pricing }: { pricing: PaymentPricing }) {
  const [openDetail, setOpenDetail] = useState<DetailKey | null>(null);

  const preview = simulatePayment({
    amount: ABOUT_SIMULATOR.previewAmount,
    method: PAYMENT_METHOD.PIX,
    pricing,
  });

  return (
    <>
      {!pricing.enabled && (
        <Alert variant="info">
          <AlertDescription>{ABOUT_COMING_SOON_NOTE}</AlertDescription>
        </Alert>
      )}

      <ListGroup>
        <ListRow label="Quem confirma" value="Automático" onPress={() => setOpenDetail('diff')} />
        <ListRow
          label="Formas"
          value="Pix · cartão · boleto"
          onPress={() => setOpenDetail('methods')}
        />
        <ListRow
          label="Taxa"
          value={`a partir de ${formatFeePercent(pricing.fee.pixPercent)}`}
          onPress={() => setOpenDetail('fee')}
        />
        <ListRow
          label="Quando cai"
          value={`${pricing.settlement.pixBusinessDays} dias úteis`}
          onPress={() => setOpenDetail('settlement')}
        />
      </ListGroup>

      <ListGroup>
        <ListRow
          label={ABOUT_SIMULATOR.title}
          value={
            preview.ok
              ? `${formatCurrency(ABOUT_SIMULATOR.previewAmount)} → ${formatCurrency(preview.netAmount)}`
              : undefined
          }
          onPress={() => setOpenDetail('simulate')}
          testID="payments-about-simulator-row"
        />
      </ListGroup>

      <ListGroup>
        <ListRow
          label={ABOUT_WHAT_WE_ASK.title}
          value={ABOUT_WHAT_WE_ASK.summary}
          onPress={() => setOpenDetail('ask')}
        />
        <ListRow label="Dúvidas" onPress={() => setOpenDetail('faq')} />
      </ListGroup>

      <DetailSheet open={openDetail !== null} onClose={() => setOpenDetail(null)}>
        {openDetail === 'diff' && <DiffDetail pricing={pricing} />}
        {openDetail === 'methods' && (
          <RowsDetail
            title="Limites e bandeiras"
            rows={getAboutLimitRows(pricing)}
            note={ABOUT_LIMITS_NOTE}
          />
        )}
        {openDetail === 'fee' && (
          <RowsDetail
            title="Taxa"
            rows={getAboutFeeRows(pricing)}
            note={getAboutFeeNote(pricing)}
          />
        )}
        {openDetail === 'settlement' && (
          <RowsDetail
            title="Quando cai"
            rows={getAboutSettlementRows(pricing)}
            note={ABOUT_SETTLEMENT_NOTE}
          />
        )}
        {openDetail === 'simulate' && <SimulatorDetail pricing={pricing} />}
        {openDetail === 'ask' && (
          <RowsDetail
            title={ABOUT_WHAT_WE_ASK.title}
            rows={ABOUT_WHAT_WE_ASK.rows}
            note={ABOUT_WHAT_WE_ASK.note}
          />
        )}
        {openDetail === 'faq' && <FaqDetail pricing={pricing} />}
      </DetailSheet>
    </>
  );
}

function ListGroup({ children }: { children: React.ReactNode }) {
  const items = React.Children.toArray(children);
  return (
    <View
      className="overflow-hidden rounded-2xl border border-neutral-900/5 bg-card"
      style={{ borderCurve: 'continuous' }}
    >
      {items.map((child, index) => (
        <View key={index} className={cn(index > 0 && 'border-t border-neutral-100')}>
          {child}
        </View>
      ))}
    </View>
  );
}

function ListRow({
  label,
  value,
  onPress,
  testID,
}: {
  label: string;
  value?: string;
  onPress: () => void;
  testID?: string;
}) {
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      testID={testID}
      className="min-h-12 flex-row items-center gap-3 px-4 py-3 active:bg-neutral-50"
    >
      <ThemedText className="flex-1 font-medium text-sm text-neutral-900">{label}</ThemedText>
      {value ? (
        <ThemedText
          className="text-[13px] text-neutral-500"
          style={{ fontVariant: ['tabular-nums'] }}
        >
          {value}
        </ThemedText>
      ) : null}
      <Ionicons className="text-neutral-300" name="chevron-forward" size={16} />
    </Pressable>
  );
}

function DetailSheet({
  open,
  onClose,
  children,
}: {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const insets = useSafeAreaInsets();
  return (
    <Modal visible={open} transparent animationType="slide" onRequestClose={onClose}>
      <Pressable className="flex-1 justify-end bg-black/40" onPress={onClose}>
        <Pressable
          onPress={(e) => e.stopPropagation()}
          className="max-h-[80%] w-full self-center rounded-t-3xl bg-card"
          style={{ maxWidth: 560, borderCurve: 'continuous' }}
        >
          <View className="items-center pb-2 pt-3">
            <View className="h-1 w-9 rounded-full bg-neutral-200" />
          </View>
          <KeyboardAwareScrollView
            bottomOffset={KEYBOARD_BOTTOM_OFFSET}
            contentContainerStyle={{ paddingHorizontal: 20, paddingBottom: insets.bottom + 24 }}
            keyboardShouldPersistTaps="handled"
          >
            {children}
          </KeyboardAwareScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

function DetailTitle({ children }: { children: string }) {
  return (
    <ThemedText className="mb-2 font-semibold text-[17px] text-neutral-900">{children}</ThemedText>
  );
}

function DetailNote({ children }: { children: string }) {
  return <ThemedText className="mt-3 text-xs leading-5 text-neutral-500">{children}</ThemedText>;
}

function KeyValue({ label, value, first }: AboutDetailRow & { first?: boolean }) {
  return (
    <View
      className={cn(
        'flex-row justify-between gap-4 py-2.5',
        !first && 'border-t border-neutral-100',
      )}
    >
      <ThemedText className="text-[13px] text-neutral-500">{label}</ThemedText>
      <ThemedText selectable className="flex-1 text-right font-medium text-[13px] text-neutral-900">
        {value}
      </ThemedText>
    </View>
  );
}

function RowsDetail({
  title,
  rows,
  note,
}: {
  title: string;
  rows: AboutDetailRow[];
  note: string;
}) {
  return (
    <View>
      <DetailTitle>{title}</DetailTitle>
      {rows.map((row, index) => (
        <KeyValue key={row.label} {...row} first={index === 0} />
      ))}
      <DetailNote>{note}</DetailNote>
    </View>
  );
}

function DiffDetail({ pricing }: { pricing: PaymentPricing }) {
  return (
    <View>
      <DetailTitle>Manual × automático</DetailTitle>
      {ABOUT_COMPARISON_ROWS.map((row, index) => (
        <View
          key={row.label}
          className={cn('gap-0.5 py-2.5', index > 0 && 'border-t border-neutral-100')}
        >
          <ThemedText className="text-[13px] text-neutral-500">{row.label}</ThemedText>
          <ThemedText className="font-medium text-sm leading-5 text-neutral-900">
            {row.automatic(pricing)}
          </ThemedText>
          <ThemedText className="text-xs text-neutral-400">{`Hoje: ${row.manual}`}</ThemedText>
        </View>
      ))}
    </View>
  );
}

function SimulatorDetail({ pricing }: { pricing: PaymentPricing }) {
  const th = useThemedHex();
  const [amountText, setAmountText] = useState(ABOUT_SIMULATOR.fieldPlaceholder);
  const amount = parseCurrency(amountText) ?? 0;

  return (
    <View>
      <DetailTitle>{ABOUT_SIMULATOR.title}</DetailTitle>
      <View className="flex-row items-baseline gap-2 border-b border-neutral-200 pb-1.5">
        <ThemedText className="font-semibold text-base text-neutral-400">R$</ThemedText>
        <TextInput
          value={amountText}
          onChangeText={(text) => setAmountText(maskCurrencyDigits(text))}
          keyboardType="number-pad"
          placeholder={ABOUT_SIMULATOR.fieldPlaceholder}
          placeholderTextColor={th('#D4D4D4')}
          accessibilityLabel="Valor da cobrança"
          testID="payments-about-simulator-amount"
          className="flex-1 font-bold text-2xl text-neutral-900"
          style={Platform.OS === 'web' ? ({ outline: 'none' } as never) : undefined}
        />
      </View>

      {amount > 0 ? (
        <View className="mt-1.5">
          {SIMULATOR_METHODS.map(({ method, label }, index) => {
            const result = simulatePayment({ amount, method, pricing });
            return (
              <View
                key={method}
                className={cn(
                  'flex-row items-center justify-between gap-3 py-2.5',
                  index > 0 && 'border-t border-neutral-100',
                )}
              >
                <View className="flex-1 gap-0.5">
                  <ThemedText className="font-medium text-sm text-neutral-900">{label}</ThemedText>
                  {result.ok && result.availableOn ? (
                    <ThemedText selectable className="text-xs text-neutral-500">
                      {`Taxa ${formatCurrency(result.feeAmount)} · cai em ${format(result.availableOn, 'dd/MM')}`}
                    </ThemedText>
                  ) : (
                    <ThemedText className="text-xs text-neutral-400">
                      {getSimulatorRejectReason(result.reason, method, pricing)}
                    </ThemedText>
                  )}
                </View>
                {result.ok && (
                  <ThemedText
                    selectable
                    className="font-bold text-base text-neutral-900"
                    style={{ fontVariant: ['tabular-nums'] }}
                  >
                    {formatCurrency(result.netAmount)}
                  </ThemedText>
                )}
              </View>
            );
          })}
        </View>
      ) : (
        <ThemedText className="mt-3 text-sm text-neutral-400">
          {ABOUT_SIMULATOR.emptyAmount}
        </ThemedText>
      )}
    </View>
  );
}

function FaqDetail({ pricing }: { pricing: PaymentPricing }) {
  const [openFaq, setOpenFaq] = useState<Set<number>>(new Set());

  const toggleFaq = (index: number) => {
    setOpenFaq((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  return (
    <View className="gap-2">
      <DetailTitle>Dúvidas</DetailTitle>
      {PAYMENT_ABOUT_FAQ.map((item, index) => (
        <CollapseSection
          key={item.question}
          title={item.question}
          expanded={openFaq.has(index)}
          onToggle={() => toggleFaq(index)}
        >
          <ThemedText className="text-sm leading-5 text-neutral-600">
            {item.answer(pricing)}
          </ThemedText>
        </CollapseSection>
      ))}
    </View>
  );
}
